import { useEffect, useMemo, useState } from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { pf } from "../utilities/helpers";
import usePageLanguage from "../hooks/usePageLanguage";
import ProductImagesComponent from "./ProductImagesComponent";
import {
  getCatalogProductDisplayFields,
  getCatalogProductDeliveryTimeLabel,
  getCatalogProductImageSlides,
  resolveCatalogProductPricing,
} from "../utilities/catalogProductAdapter";
import { getAdjustmentDisplayName } from "../utilities/locationProductAdjustments";
import type { Product } from "../interfaces/catalogProduct";
import {
  emptyAttributeSelection,
  getAttributeName,
  getAttributeValueLabel,
  getSellableProductUuid,
  getSelectableValues,
  getVariantPriceRange,
  hasVariants,
  resolveDisplayCatalogProduct,
  resolveVariant,
} from "../utilities/productCatalog";
import React from "react";

type ProductPanelProps = {
  product: Product;
  id: number | string;
  initialAttributes?: Record<string, string>;
};

export default function ProductPanel({
  product,
  id,
  initialAttributes,
}: ProductPanelProps) {
  const locale = usePageLanguage();
  const { increaseCartQuantity, pricingNow, products } = useShoppingCart();

  const [tab, setTab] = useState("descriptionTab");
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >(() => ({
    ...emptyAttributeSelection(product),
    ...initialAttributes,
  }));

  useEffect(() => {
    setSelectedAttributes({
      ...emptyAttributeSelection(product),
      ...initialAttributes,
    });
  }, [product.productUuid, initialAttributes]);

  const displayProduct = useMemo(
    () => resolveDisplayCatalogProduct(product, products, selectedAttributes),
    [product, products, selectedAttributes]
  );

  const display = useMemo(
    () => getCatalogProductDisplayFields(displayProduct, locale),
    [displayProduct, locale]
  );

  const imageSlides = useMemo(
    () => getCatalogProductImageSlides(displayProduct),
    [displayProduct]
  );

  const deliveryTimeLabel = useMemo(
    () => getCatalogProductDeliveryTimeLabel(displayProduct, locale),
    [displayProduct, locale]
  );

  const resolvedVariant = useMemo(
    () => resolveVariant(product, selectedAttributes),
    [product, selectedAttributes]
  );

  const parentPricing = useMemo(
    () => resolveCatalogProductPricing(product, pricingNow),
    [product, pricingNow]
  );
  const selectedProductPricing = useMemo(
    () => resolveCatalogProductPricing(displayProduct, pricingNow),
    [displayProduct, pricingNow]
  );

  const variantMode = hasVariants(product);
  const priceRange = useMemo(
    () => getVariantPriceRange(product),
    [product]
  );

  const areAllAttributesSelected = useMemo(() => {
    const attrs = product.productAttributes ?? [];
    if (!attrs.length) {
      return true;
    }
    return attrs.every((a) => {
      const v = selectedAttributes[a.id];
      return typeof v === "string" && v !== "";
    });
  }, [product.productAttributes, selectedAttributes]);

  const displayPrice = useMemo(() => {
    if (variantMode) {
      if (resolvedVariant) {
        return {
          regular: displayProduct.priceRegular,
          total: selectedProductPricing.price_total,
          hasDiscount: selectedProductPricing.activeAdjustments.length > 0,
          adjustments: selectedProductPricing.activeAdjustments,
        };
      }
      return null;
    }
    return {
      regular: product.priceRegular,
      total: parentPricing.price_total,
      hasDiscount: parentPricing.activeAdjustments.length > 0,
      adjustments: parentPricing.activeAdjustments,
    };
  }, [
    variantMode,
    resolvedVariant,
    displayProduct.priceRegular,
    selectedProductPricing,
    parentPricing,
    product.priceRegular,
  ]);

  const languageTranslations = {
    et: {
      add_to_cart: "LISA OSTUKORVI",
      description: "Kirjeldus",
      delivery: "Tarne",
    },
    en: {
      add_to_cart: "ADD TO CART",
      description: "Description",
      delivery: "Delivery",
    },
  };

  const sellableUuid = getSellableProductUuid(product, selectedAttributes);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!sellableUuid) {
      return;
    }
    increaseCartQuantity(
      sellableUuid,
      undefined,
      undefined,
      false,
      undefined,
      undefined,
    );
  };

  return (
    <div className="panel" id="productPanel">
      <div className="panel-body container">
        <div className="col-xs-12">
          <div className="imagesContainer">
            <h1 className="product-name">{display.displayName}</h1>
            <div className="web-product-image">
              <ProductImagesComponent
                id={id}
                slides={imageSlides}
                options={{ align: "start" }}
              />
            </div>
          </div>

          <div className="dataContainer">
            <h1 className="product-name inline-block">
              {display.displayName}
            </h1>

            {variantMode && !areAllAttributesSelected && priceRange && (
              <div className="productPrice">
                {priceRange.minPrice !== priceRange.maxPrice ? (
                  <>
                    {pf(priceRange.minPrice)} &euro; - {pf(priceRange.maxPrice)}{" "}
                    &euro;
                  </>
                ) : (
                  <>{pf(priceRange.minPrice)} &euro;</>
                )}
              </div>
            )}

            {displayPrice && (
              <>
                <div
                  className="productPrice"
                  style={
                    displayPrice.hasDiscount
                      ? { textDecoration: "line-through", lineHeight: "1" }
                      : {}
                  }
                >
                  {pf(displayPrice.regular)} &euro;
                </div>
                {displayPrice.hasDiscount && "adjustments" in displayPrice && (
                  <div className="discount_price_box shop-controlled-border-radius discount-price-color">
                    {pf(Number(displayPrice.total))} &euro;{" "}
                    {displayPrice.adjustments?.map((adjustment, i) => {
                      const string = `${i > 0 ? ", " : ""}${getAdjustmentDisplayName(
                        adjustment
                      )} ${
                        adjustment.value.toString().startsWith("-") ? "" : "+"
                      }${adjustment.value}%`;
                      return string;
                    })}
                  </div>
                )}
              </>
            )}

            <div>
              <button
                className={`btn btn-primary ${
                  variantMode ? "addProductVariableToCart" : "addProductToCart"
                } shop-controlled-border-radius`}
                style={{ fontSize: "14px", fontWeight: variantMode ? 600 : undefined }}
                disabled={variantMode && !areAllAttributesSelected}
                onClick={handleAddToCart}
              >
                {languageTranslations[locale]?.add_to_cart}
              </button>
            </div>

            {variantMode && (product.productAttributes?.length ?? 0) > 0 && (
              <div className="product-variants">
                {product.productAttributes!.map((attribute) => {
                  const possibleValues = getSelectableValues(
                    product,
                    selectedAttributes,
                    attribute.id
                  );

                  return (
                    <div
                      key={attribute.id}
                      className="product-variant"
                      style={{ marginTop: "10px" }}
                    >
                      <span style={{ fontSize: "15px", fontWeight: "600" }}>
                        {getAttributeName(attribute, locale)}
                      </span>
                      <div className="product-variant-values">
                        {attribute.values.map((valueKey) => {
                          const isDisabled = !possibleValues.includes(valueKey);
                          const isSelected =
                            selectedAttributes[attribute.id] === valueKey;

                          return (
                            <button
                              key={valueKey}
                              type="button"
                              disabled={
                                (product.productAttributes?.length ?? 0) > 1 &&
                                isDisabled
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAttributes((prev) => ({
                                  ...prev,
                                  [attribute.id]:
                                    prev[attribute.id] === valueKey
                                      ? ""
                                      : valueKey,
                                }));
                              }}
                              className={`btn btn-primary product-variant-value shop-controlled-border-radius ${
                                isSelected ? "product-variant-value-selected" : ""
                              }`}
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                marginLeft: "5px",
                                margin: "0px",
                                minWidth: "45px",
                                padding: "5px 15px",
                                backgroundColor: isSelected
                                  ? "var(--shop-theme-color)!important"
                                  : "",
                              }}
                            >
                              {getAttributeValueLabel(
                                attribute,
                                locale,
                                valueKey
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-15">
              <div className="nav-tabs-horizontal" data-plugin="tabs">
                <ul className="nav nav-tabs nav-tabs-line" role="tablist">
                  <li className="nav-item" role="presentation">
                    <a
                      className={`nav-link${
                        tab === "descriptionTab" ? " active" : ""
                      }`}
                      onClick={() => setTab("descriptionTab")}
                      href={undefined}
                      aria-controls="descriptionTab"
                      role="tab"
                    >
                      {languageTranslations[locale]?.description}
                    </a>
                  </li>
                  <li className="nav-item" role="presentation">
                    <a
                      className={`nav-link${
                        tab === "deliveryTab" ? " active" : ""
                      }`}
                      onClick={() => setTab("deliveryTab")}
                      href={undefined}
                      aria-controls="deliveryTab"
                      role="tab"
                    >
                      {languageTranslations[locale]?.delivery}
                    </a>
                  </li>
                </ul>
                <div className="tab-content pt-20">
                  <div
                    className={`tab-pane text-left${
                      tab === "descriptionTab" ? " active" : ""
                    }`}
                    id="descriptionTab"
                    role="tabpanel"
                  >
                    <div
                      style={{ fontSize: "16px" }}
                      dangerouslySetInnerHTML={{
                        __html: display.description,
                      }}
                    />
                  </div>
                  <div
                    className={`tab-pane text-left${
                      tab === "deliveryTab" ? " active" : ""
                    }`}
                    id="deliveryTab"
                    role="tabpanel"
                  >
                    {deliveryTimeLabel && (
                      <div className="deliveryTime">
                        {deliveryTimeLabel}
                      </div>
                    )}
                    {display.deliveryInfo && (
                      <div
                        className="deliveryInfo"
                        style={{ fontSize: "16px", whiteSpace: "break-spaces" }}
                      >
                        {display.deliveryInfo}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
